import { computed, ref } from "vue";

import type { DsComponentRenderProp } from "@devsalsa/vue-core";

import type { Marketplace } from "@/core/modules/account/services/AccountService.types";

import AssetsService from "@/shared/services/AssetsService";

import type { MarketplacesCertifications } from "@/shared/services/AssetsService.types";

import { MarketplaceCode } from "@/shared/enums/MarketplaceCodeEnum";
import { PaymentPlatform } from "@/shared/enums/PaymentPlatformEnum";

import AUWireInfo from "@/shared/helpers/marketplaces/wire/AUWireInfo.vue";
import CAWireInfo from "@/shared/helpers/marketplaces/wire/CAWireInfo.vue";
import EUWireInfo from "@/shared/helpers/marketplaces/wire/EUWireInfo.vue";
import UKWireInfo from "@/shared/helpers/marketplaces/wire/UKWireInfo.vue";
import USWireInfo from "@/shared/helpers/marketplaces/wire/USWireInfo.vue";

import AUCostsFlagIcon from "@/shared/components/icon/marketplaces/AUCostsFlagIcon.vue";
import AUFlagCircleIcon from "@/shared/components/icon/marketplaces/AUFlagCircleIcon.vue";
import CACostsFlagIcon from "@/shared/components/icon/marketplaces/CACostsFlagIcon.vue";
import CAFlagCircleIcon from "@/shared/components/icon/marketplaces/CAFlagCircleIcon.vue";
import DECostsFlagIcon from "@/shared/components/icon/marketplaces/DECostsFlagIcon.vue";
import DEFlagCircleIcon from "@/shared/components/icon/marketplaces/DEFlagCircleIcon.vue";
import UKCostsFlagIcon from "@/shared/components/icon/marketplaces/UKCostsFlagIcon.vue";
import UKFlagCircleIcon from "@/shared/components/icon/marketplaces/UKFlagCircleIcon.vue";
import USCostsFlagIcon from "@/shared/components/icon/marketplaces/USCostsFlagIcon.vue";
import USFlagCircleIcon from "@/shared/components/icon/marketplaces/USFlagCircleIcon.vue";

import {
  getMarketplaceLocale,
  isMarketplace,
  marketplaceDateTimeFormats,
  marketplaceMessages,
  marketplaceNumberFormats,
} from "@/shared/config/marketplaceConfig";

const marketplacesAssets = ref<Marketplace[]>([]);

export function useMarketplace() {
  const currentMarketplace = () => {
    if (isMarketplace(MarketplaceCode.Ca)) {
      return MarketplaceCode.Ca;
    }
    if (isMarketplace(MarketplaceCode.Uk)) {
      return MarketplaceCode.Uk;
    }
    if (isMarketplace(MarketplaceCode.Au)) {
      return MarketplaceCode.Au;
    }
    if (isMarketplace(MarketplaceCode.De)) {
      return MarketplaceCode.De;
    }
    return MarketplaceCode.Us;
  };

  const marketplaceIcon = (marketplace: string) => {
    if (marketplace === MarketplaceCode.Ca) {
      return CAFlagCircleIcon as DsComponentRenderProp;
    }
    if (marketplace === MarketplaceCode.Au) {
      return AUFlagCircleIcon as DsComponentRenderProp;
    }
    if (marketplace === MarketplaceCode.Uk) {
      return UKFlagCircleIcon as DsComponentRenderProp;
    }
    if (marketplace === MarketplaceCode.De) {
      return DEFlagCircleIcon as DsComponentRenderProp;
    }
    return USFlagCircleIcon as DsComponentRenderProp;
  };

  const marketplaceCostIcon = (marketplace: string) => {
    if (marketplace === MarketplaceCode.Ca) {
      return CACostsFlagIcon as DsComponentRenderProp;
    }
    if (marketplace === MarketplaceCode.Au) {
      return AUCostsFlagIcon as DsComponentRenderProp;
    }
    if (marketplace === MarketplaceCode.Uk) {
      return UKCostsFlagIcon as DsComponentRenderProp;
    }
    if (marketplace === MarketplaceCode.De) {
      return DECostsFlagIcon as DsComponentRenderProp;
    }
    return USCostsFlagIcon as DsComponentRenderProp;
  };

  const currentMarketplaceWireInfo = () => {
    if (currentMarketplace() === MarketplaceCode.Ca) {
      return CAWireInfo as DsComponentRenderProp;
    }
    if (currentMarketplace() === MarketplaceCode.Uk) {
      return UKWireInfo as DsComponentRenderProp;
    }
    if (currentMarketplace() === MarketplaceCode.Au) {
      return AUWireInfo as DsComponentRenderProp;
    }
    if (currentMarketplace() === MarketplaceCode.De) {
      return EUWireInfo as DsComponentRenderProp;
    }
    return USWireInfo as DsComponentRenderProp;
  };

  const currentMarketplaceIcon = computed(() =>
    marketplaceIcon(currentMarketplace())
  );

  const currentMarketplaceCostIcon = computed(() =>
    marketplaceCostIcon(currentMarketplace())
  );

  const currentMarketplaceName = computed(() =>
    currentMarketplace().toUpperCase()
  );

  const zipCodeMask = (marketplace: string) => {
    if (marketplace === MarketplaceCode.Au) {
      return { mask: "####", tokens: {} };
    }

    if (marketplace === MarketplaceCode.Uk) {
      return { mask: "**** ***", tokens: {} };
    }

    if (marketplace === MarketplaceCode.De) {
      return { mask: "#####", tokens: {} };
    }

    if (marketplace === MarketplaceCode.Ca) {
      return { mask: "@#@ #@#", tokens: {} };
    }

    return { mask: "#####", tokens: {} };
  };

  const zipCodeMaskPlaceholder = (marketplace: string) => {
    if (marketplace === MarketplaceCode.Au) {
      return "####";
    }

    if (marketplace === MarketplaceCode.Uk) {
      return "#### ###";
    }

    if (marketplace === MarketplaceCode.De) {
      return "#####";
    }

    if (marketplace === MarketplaceCode.Ca) {
      return "### ###";
    }

    return "#####";
  };

  const isTikTokShopMarketplace = computed(() => {
    return (
      isMarketplace(MarketplaceCode.Us) ||
      isMarketplace(MarketplaceCode.Uk) ||
      isMarketplace(MarketplaceCode.De)
    );
  });

  const isTikTokAdCenterMarketplace = computed(() => {
    return isMarketplace(MarketplaceCode.Us);
  });

  const isReimbursementTaxesMarketplace = computed(() => {
    return (
      isMarketplace(MarketplaceCode.Us) || isMarketplace(MarketplaceCode.Ca)
    );
  });

  const isStripeMarketplace = computed((): boolean => {
    const currentMarketplaceAsset = marketplacesAssets.value.find(
      (marketplaceAsset) => marketplaceAsset.short_code === currentMarketplace()
    );
    return (
      currentMarketplaceAsset?.default_payment_platform ===
      PaymentPlatform.Stripe
    );
  });

  const marketplaceCertifications = async () => {
    const marketplaceCertifications: MarketplacesCertifications =
      await AssetsService.getCertifications();
    return marketplaceCertifications[
      currentMarketplace() as keyof MarketplacesCertifications
    ];
  };

  const getMarketplaceAssets = async (
    update = false
  ): Promise<Marketplace[]> => {
    if (update || marketplacesAssets.value.length === 0) {
      marketplacesAssets.value = await AssetsService.getMarketplaces();
    }
    return marketplacesAssets.value;
  };

  return {
    datetimeFormats: marketplaceDateTimeFormats,
    numberFormats: marketplaceNumberFormats,
    messages: marketplaceMessages,
    isMarketplace,
    getLocale: getMarketplaceLocale,
    marketplaceIcon,
    currentMarketplaceWireInfo,
    currentMarketplace,
    marketplaceCertifications,
    currentMarketplaceIcon,
    currentMarketplaceName,
    zipCodeMask,
    zipCodeMaskPlaceholder,
    isTikTokShopMarketplace,
    isTikTokAdCenterMarketplace,
    isReimbursementTaxesMarketplace,
    isStripeMarketplace,
    currentMarketplaceCostIcon,
    getMarketplaceAssets,
  };
}
